import { useTranslation } from '@rhino-project/core/i18n';
import { AuthForm } from '../../components/auth/AuthForm';
import { OmniAuthButton } from '../../components/buttons/omniauth';
import { SubmitButton } from '../../components/buttons';
import {
  useAcceptInvitationAction,
  useInvitation
} from '@rhino-project/core/queries';
import { oauthProviders } from '@rhino-project/core/utils';
import { NetworkParamError } from '@rhino-project/core/lib';
import { AuthPage, type AuthPageProps } from './AuthPage';
import {
  Alert,
  AlertContent,
  AlertTitle,
  Separator,
  Spinner
} from '@heroui/react';
import { useNavigate, useSearch } from '@tanstack/react-router';

export const AcceptInvitationPage = (props: AuthPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useSearch({ strict: false }) as {
    invitation_token?: string;
  };
  const invitationToken = params.invitation_token ?? '';

  const {
    data: invitationData,
    error: showError,
    isLoading: isInvitationLoading
  } = useInvitation(invitationToken);
  const invitation = invitationData?.data;
  const passwordRequired = invitation?.password_required;

  const {
    mutate: acceptAction,
    isPending,
    error
  } = useAcceptInvitationAction();
  const paramError = (error || showError) as NetworkParamError | null;

  const invitationError =
    paramError?.errors &&
    !Array.isArray(paramError.errors) &&
    paramError.errors['invitation_token'];

  // No token at all (blank param disables the show query) or an invalid-token
  // error means there is nothing to accept — surface the danger alert and skip
  // the accept form/button entirely so there's no dead submit.
  const showInvalidToken = !invitationToken || Boolean(invitationError);

  // Token present and the show query is still in flight — don't render either
  // accept branch until we know whether a password is required.
  const showLoading = !showInvalidToken && isInvitationLoading && !invitation;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = (formValues: any) => {
    acceptAction(
      { ...formValues, ...params },
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onSuccess: (response: any) => {
          // A session in the response means accept minted one (new account, or
          // the invitee was already signed in): the /auth guard intentionally
          // does NOT redirect off the accept page, so we navigate into the app
          // explicitly here. No session => existing account, and an invite is
          // never a magic sign-in link, so route to sign in instead.
          navigate({ to: response?.data?.data ? '/' : '/auth/signin' });
        }
      }
    );
  };

  // Loading: token present, metadata still loading — show only a spinner.
  if (showLoading) {
    return (
      <AuthPage
        title={t('rhino.frontend.messages.accept_invitation')}
        {...props}
      >
        <div className="flex justify-center py-4">
          <Spinner
            aria-label={t('rhino.frontend.messages.loading_invitation')}
          />
        </div>
      </AuthPage>
    );
  }

  // Invalid/missing token: show the danger alert, no accept form/button.
  if (showInvalidToken) {
    return (
      <AuthPage
        title={t('rhino.frontend.messages.accept_invitation')}
        {...props}
      >
        <Alert
          status="danger"
          className="mb-4 bg-danger/10 border border-danger/20"
        >
          <AlertContent>
            <AlertTitle>
              {invitationError
                ? t('rhino.frontend.messages.invitation_token_value', {
                    value1: invitationError
                  })
                : t(
                    'rhino.frontend.messages.this_invitation_link_is_invalid_or_has_expired'
                  )}
            </AlertTitle>
          </AlertContent>
        </Alert>
      </AuthPage>
    );
  }

  return (
    <AuthPage
      title={t('rhino.frontend.messages.accept_invitation')}
      description={
        passwordRequired
          ? t(
              'rhino.frontend.messages.enter_a_name_and_password_to_create_your_account'
            )
          : t('rhino.frontend.messages.accept_this_invitation_for_value', {
              value1: invitation?.email ?? 'your account'
            })
      }
      {...props}
    >
      {passwordRequired ? (
        <>
          <AuthForm
            nameField
            passwordField
            passwordConfirmField
            primaryAction={t('rhino.frontend.messages.accept_invitation_title')}
            isPending={isPending}
            errors={paramError?.errors}
            onSubmit={handleSubmit}
            {...props}
          />
          {oauthProviders().length > 0 && (
            <>
              <Separator className="my-4" />
              <div className="flex flex-col gap-2">
                {oauthProviders().map((p) => (
                  <OmniAuthButton
                    key={p.name}
                    provider={p.name}
                    providerPath={p.path}
                    params={{ invitation_token: invitationToken }}
                  />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <SubmitButton isPending={isPending} onPress={() => handleSubmit({})}>
          {t('rhino.frontend.messages.accept_invitation_title')}
        </SubmitButton>
      )}
    </AuthPage>
  );
};
